import { useState } from "react";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, whatsappLink } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, type: "enquiry", source: "website" });
      toast.success("Message received! We will get back to you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-navy-base min-h-screen" data-testid="contact-page">
      <SEO title="Contact XTM TECHNOLOGIES Jorhat — The FutureSkills Academy"
        description="Contact XTM TECHNOLOGIES, Katyayani Building, Tarajan, Jorhat, Assam. Call +91 9101380454 or WhatsApp for course enquiries, admissions and career counselling." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Contact</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white" data-testid="contact-heading">Visit or Reach the Academy</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="bg-card border border-white/10 rounded-2xl p-7 mb-6">
            <h2 className="font-display text-xl font-bold text-white mb-1">XTM TECHNOLOGIES</h2>
            <p className="text-xs uppercase tracking-[0.18em] text-orange-400 font-semibold mb-5">The FutureSkills Academy</p>
            <ul className="space-y-4 text-sm text-slate-300">
              <li className="flex gap-3"><MapPin className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" /> Katyayani Building, 3rd Floor, Near Star Media, Tarajan, Jorhat – 785001, Assam</li>
              <li><a href="tel:+919101380454" className="flex gap-3 hover:text-orange-300 transition-colors" data-testid="contact-phone"><Phone className="w-4 h-4 text-orange-400 shrink-0" /> +91 9101380454</a></li>
              <li><a href="mailto:info@xtmtechnologies.in" className="flex gap-3 hover:text-orange-300 transition-colors" data-testid="contact-email"><Mail className="w-4 h-4 text-orange-400 shrink-0" /> info@xtmtechnologies.in</a></li>
              <li><span className="flex gap-3"><MessageCircle className="w-4 h-4 text-orange-400 shrink-0" /> xtmtechnologies.in</span></li>
            </ul>
            <div className="flex flex-wrap gap-3 mt-6">
              <Button asChild className="bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold rounded-full transition-colors" data-testid="contact-whatsapp-btn">
                <a href={whatsappLink("Hello XTM TECHNOLOGIES, I have a question about your courses.")} target="_blank" rel="noreferrer"
                  onClick={() => api.post("/leads/whatsapp-click", { context: "contact-page" }).catch(() => {})}>
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full transition-colors" data-testid="contact-call-btn">
                <a href="tel:+919101380454"><Phone className="w-4 h-4 mr-2" /> Call Now</a>
              </Button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10 h-72" data-testid="contact-map">
            <iframe title="XTM TECHNOLOGIES location — Tarajan, Jorhat" width="100%" height="100%" style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg) saturate(0.7)" }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              src="https://www.openstreetmap.org/export/embed.html?bbox=94.2000%2C26.7300%2C94.2400%2C26.7600&layer=mapnik&marker=26.7450%2C94.2200" />
          </div>
        </div>

        <div className="bg-card border border-white/10 rounded-2xl p-7 h-fit">
          <h2 className="font-display text-xl font-bold text-white mb-6">Send Us a Message</h2>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div>
              <Label htmlFor="ct-name">Full Name *</Label>
              <Input id="ct-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="contact-name-input" />
            </div>
            <div>
              <Label htmlFor="ct-phone">Phone *</Label>
              <Input id="ct-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="contact-phone-input" />
            </div>
            <div>
              <Label htmlFor="ct-email">Email</Label>
              <Input id="ct-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="contact-email-input" />
            </div>
            <div>
              <Label htmlFor="ct-msg">Message *</Label>
              <Textarea id="ct-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-navy-base border-white/15 text-white mt-1" data-testid="contact-message-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold transition-colors" data-testid="contact-submit-btn">
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
